import { makeAutoObservable, runInAction } from 'mobx';
import type { FHRequest, FHRequestMessage } from '../types';
import { managerApi } from '../api/manager_api';

export class ManagerQueueStore {
  // Queue of requests needing manager attention
  queue: FHRequest[] = [];
  loading = false;
  cursor: string | null = null;
  hasMore = true;

  // Error state
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Actions
  clearError(): void {
    this.error = null;
  }

  /**
   * Fetch manager queue (requests needing manager attention)
   */
  async fetchQueue(refresh = false): Promise<void> {
    if (this.loading) return;
    if (!refresh && !this.hasMore) return;

    runInAction(() => {
      this.loading = true;
      this.error = null;
      if (refresh) {
        this.cursor = null;
        this.hasMore = true;
      }
    });

    try {
      const cursor = refresh ? undefined : this.cursor || undefined;
      const response = await managerApi.getQueue(cursor);

      runInAction(() => {
        if (refresh) {
          this.queue = response.items;
        } else {
          this.queue = [...this.queue, ...response.items];
        }
        this.cursor = response.cursor;
        this.hasMore = response.has_more;
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch manager queue';
      });
    }
  }

  /**
   * Send a note to the assistant and resolve the attention flag
   */
  async respondToRequest(
    requestKey: string,
    message: string,
  ): Promise<{ request: FHRequest; message: FHRequestMessage } | undefined> {
    try {
      const result = await managerApi.respondToRequest(requestKey, message);

      runInAction(() => {
        // Remove from queue after responding
        this.queue = this.queue.filter((r) => r.key !== requestKey);
      });

      return result;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to respond to request';
      });
      return undefined;
    }
  }

  /**
   * Close request without notifying client
   */
  async closeQuietly(requestKey: string): Promise<FHRequest | undefined> {
    try {
      const request = await managerApi.closeQuietly(requestKey);

      runInAction(() => {
        // Remove from queue after closing
        this.queue = this.queue.filter((r) => r.key !== requestKey);
      });

      return request;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to close request';
      });
      return undefined;
    }
  }

  /**
   * Clear attention flag without sending a message
   */
  async resolveAttention(requestKey: string): Promise<FHRequest | undefined> {
    try {
      const request = await managerApi.resolveAttention(requestKey);

      runInAction(() => {
        // Remove from queue after resolving
        this.queue = this.queue.filter((r) => r.key !== requestKey);
      });

      return request;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to resolve attention';
      });
      return undefined;
    }
  }

  /**
   * Reset store state
   */
  reset(): void {
    this.queue = [];
    this.cursor = null;
    this.hasMore = true;
    this.error = null;
  }
}

export const managerQueueStore = new ManagerQueueStore();
